import React from 'react';
import { useWindowDimensions } from 'react-native';
import { DNAButton, DNAText, DNAButtonProps } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import { useLocation } from 'react-router';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import withPermissionsAccessControl from 'src/hooks/useFeatureFlags/withPermissionsAccessControl';
import { useDispatch } from 'src/state/redux';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { EDITOR_TYPE, presentationBuilderActions } from 'src/state/redux/slice/PresentationBuilder/PresentationBuilder';
import { useAllFolders, useAllFoldersInstance } from 'src/state/redux/selector/folder';
import { isSharedEditableFolder } from 'src/utils/foldersHelpers';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { shouldUseHeaderIconButtons } from 'src/components/Header/SearchInput/SearchTypeToggle';

type DeviceModeButtonProps = Record<DeviceMode, DNAButtonProps>

const baseButtonProps: DNAButtonProps = {
  appearance: 'outline',
  status: 'tertiary',
  iconLeft: 'stack-plus',
}

const deviceModeButtonHeaderProps: DeviceModeButtonProps = {
  desktop: {
    ...baseButtonProps,
    size: 'lg',
  },
  tablet: {
    appearance:'ghost',
    status:'subtle',
    iconLeft: 'stack-plus',
    size:'xl',
    rounded: true,
    colorMode: 'dark',
  },
}

const deviceModeButtonMyFoldersProps:DeviceModeButtonProps = {
  desktop: {
    ...baseButtonProps,
  },
  tablet: {
    ...baseButtonProps,
    size:'xl',
  },
}

export type CustomDeckButtonMode = 'header' | 'myFolders'

export interface UseCustomDeckButtonLogicReturn {
  onCreatePresentation: () => void;
  shouldRender: boolean;
  folder?: any; // Type should match the folder type from your codebase
}

export interface UseCustomDeckButtonPropsReturn {
  buttonProps: DNAButtonProps;
}

/**
 * Hook for handling business logic related to custom deck button
 * Manages folder detection, presentation creation, and render conditions
 */
export const useCustomDeckButtonLogic = (mode: CustomDeckButtonMode): UseCustomDeckButtonLogicReturn => {
  const dispatch = useDispatch();
  const ownFolders = useAllFolders();
  const sharedFolders = useAllFoldersInstance(undefined, true)
  const location = useLocation();
  const enableEditSharedFolder = useFeatureFlags('enableEditSharedFolder');
  const currentUser = useCurrentUser()
  const sharedFoldersTab = !!location?.pathname.includes('shared_folders')

  const getFolder = () => {
    const pathTokens = location.pathname.split('/').slice(1)
    if (pathTokens.length === 0 || mode === 'header') {
      return;
    }

    const folderId = pathTokens.reverse()[0];
    let folderORMFromPath = ownFolders
      .find(folderORM => folderORM.model.id === folderId)
    if (folderORMFromPath) {
      return folderORMFromPath
    }
    folderORMFromPath = sharedFolders
      .find(folderORM => folderORM.model.id === folderId)

    if (folderORMFromPath &&
        isSharedEditableFolder(folderORMFromPath, currentUser.userProfile?.id) && enableEditSharedFolder) {
      return folderORMFromPath
    }
  }

  const folder = getFolder()

  const onCreatePresentation = (): void => {
    dispatch(presentationBuilderActions.openPresentationBuilderNew({
      targetFolder: folder,
      editorType: EDITOR_TYPE.OWNER,
    }))
  };
  // [TODO-PERM] - Inline permissions
  const shouldRender = !(sharedFoldersTab && mode === 'myFolders' && !folder)

  return {
    onCreatePresentation,
    shouldRender,
    folder,
  }
}

/**
 * Hook for handling button properties and styling
 * Manages device-specific props, feature flags, and layout considerations
 */
export const useCustomDeckButtonProps = (mode: CustomDeckButtonMode): UseCustomDeckButtonPropsReturn => {
  const { deviceMode, isCompactLayoutDeprecate } = useAppSettings()
  const { width } = useWindowDimensions();
  const isDesktop = deviceMode === 'desktop'
  const shouldUseIconButton = mode === 'header'
    ? shouldUseHeaderIconButtons(width)
    : isCompactLayoutDeprecate;

  const currentDeviceModeButtonProps = mode === 'header'
    ? deviceModeButtonHeaderProps[deviceMode]
    : deviceModeButtonMyFoldersProps[deviceMode]

  const baseButtonPropsWithChildren = {
    ...currentDeviceModeButtonProps,
    children: mode === 'myFolders' && isDesktop
      ? 'Create presentation'
      : undefined,
  }

  const buttonProps: DNAButtonProps = {
    ...baseButtonPropsWithChildren,
    appearance: 'filled' as const,
    status: 'primary' as const,
    size: deviceMode === 'desktop' ? ('md' as const) : ('xl' as const),
    iconLeft: shouldUseIconButton ? 'stack-plus' : undefined,
    rounded: false,
    colorMode: 'light' as const,
    children: shouldUseIconButton ? undefined : 'Create presentation',
  }

  return {
    buttonProps,
  }
}

export interface UseCustomDeckButtonReturn {
  onCreatePresentation: () => void;
  buttonProps: DNAButtonProps;
  shouldRender: boolean;
  children?: string;
}

/**
 * Main hook that combines logic and props hooks
 * Maintains backward compatibility with existing interface
 */
export const useCustomDeckButton = (mode: CustomDeckButtonMode): UseCustomDeckButtonReturn => {
  const { onCreatePresentation, shouldRender } = useCustomDeckButtonLogic(mode);
  const { buttonProps } = useCustomDeckButtonProps(mode);

  return {
    onCreatePresentation,
    buttonProps,
    shouldRender,
  }
}

export type CustomDeckButtonProps = {
  mode: CustomDeckButtonMode,
}

const CustomDeckButtonBase: React.FC<CustomDeckButtonProps & DNAButtonProps> = (props) => {
  const { mode, ...buttonPropsFromParent } = props;
  const { isPWAStandalone } = useAppSettings();
  const {
    onCreatePresentation,
    buttonProps,
    shouldRender,
  } = useCustomDeckButton(mode);
  const shouldUseIconButton = !buttonProps.children;

  if (!shouldRender) {
    return null;
  }

  return (
    <DNAPopover disablePopover={!shouldUseIconButton || mode === 'myFolders' || isPWAStandalone}>
      <DNAPopover.Anchor>
        <DNAButton
          {...buttonProps}
          {...buttonPropsFromParent}
          testID="create-presentation-btn"
          onPress={onCreatePresentation}
        />
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Create presentation</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

export const CustomDeckButton = withPermissionsAccessControl(CustomDeckButtonBase, {
  flags: ['BEAC_6655_custom_deck_usage'],
});

export default CustomDeckButton
